import { ReactNode } from "react";
import { Typography } from "@/components/atoms";
import { clsx } from "@/utils";

export type PriceSummaryRow = {
  label: string;
  /** Omit for a bare section label (e.g. "Extras" above its indented line items). */
  value?: string;
  indent?: boolean;
  /** @deprecated Use `PriceSummary`'s `totalRow` for an interactive value. Renders in place of `value`. */
  node?: ReactNode;
};

const Row = ({ label, value, indent, node }: PriceSummaryRow) => (
  <div className="flex items-center justify-between gap-3">
    <Typography variant="paragraph" className={clsx("min-w-0 text-neutral-dark-gray", indent && "pl-3")}>
      {label}
    </Typography>
    {node ? (
      <div className="shrink-0">{node}</div>
    ) : (
      value && (
        <Typography variant="action" className="shrink-0 whitespace-nowrap">
          {value}
        </Typography>
      )
    )}
  </div>
);

// Static sample kept as the default so the confirmation page's layout-only usage
// (DEV-449 rebuilds it with real data) renders unchanged.
const SAMPLE_GROUPS: PriceSummaryRow[][] = [
  [{ label: "Journey Cost", value: "$294.68" }, { label: "Extras" }, { label: "Baby Seat", value: "+ $1.32", indent: true }],
  [
    { label: "Service Fee", value: "+ $68.08" },
    { label: "Credit Card Processing Fee", value: "+ $13.52" },
  ],
];

interface PriceSummaryProps {
  /** Row groups separated by hairline dividers. Defaults to the static confirmation sample. */
  groups?: PriceSummaryRow[][];
  total?: string;
  totalRow?: ReactNode;
  className?: string;
}

/** Price breakdown — quote-driven in the Booking Summary, static sample on the confirmation page. */
export const PriceSummary = ({ groups = SAMPLE_GROUPS, total = "$377.60", totalRow, className }: PriceSummaryProps) => {
  const visibleGroups = groups.filter((group) => group.length > 0);
  return (
    <div className={clsx("flex flex-col", className)}>
      <Typography variant="title">Price Summary</Typography>

      {visibleGroups.map((group, index) => (
        <div key={index}>
          {index > 0 && <div className="mb-3 border-t border-neutral-mid-gray" />}
          {/* 8px from the header, 8px between rows */}
          <div className={clsx("mb-3 flex flex-col gap-2", index === 0 && "mt-2")}>
            {group.map((row, rowIndex) => (
              <Row key={`${row.label}-${rowIndex}`} {...row} />
            ))}
          </div>
        </div>
      ))}

      <div className={clsx("border-t border-neutral-mid-gray", visibleGroups.length === 0 && "mt-2")} />

      <div className="mt-3">
        {totalRow ?? (
          <div className="flex items-center justify-between gap-3">
            <Typography variant="title">Total</Typography>
            <Typography variant="h2">{total}</Typography>
          </div>
        )}
      </div>
    </div>
  );
};
